import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearAuth } from '../auth';
import { authUtils } from '../config/cognito';

interface SessionConfig {
  sessionTimeoutMinutes: number; // Total session duration (1 hour)
  idleTimeoutMinutes: number; // Idle warning time (30 minutes)
  warningCountdownSeconds: number; // Warning countdown (2 minutes)
}

interface SessionState {
  isActive: boolean;
  isIdle: boolean;
  isWarning: boolean;
  timeRemaining: number;
  warningTimeRemaining: number;
}

const DEFAULT_CONFIG: SessionConfig = {
  sessionTimeoutMinutes: 60, // 1 hour total session
  idleTimeoutMinutes: 30, // 30 minutes idle warning
  warningCountdownSeconds: 120, // 2 minutes warning countdown
};

export function useSessionManager(config: SessionConfig = DEFAULT_CONFIG) {
  const navigate = useNavigate();
  const [sessionState, setSessionState] = useState<SessionState>({
    isActive: true,
    isIdle: false,
    isWarning: false,
    timeRemaining: config.sessionTimeoutMinutes * 60,
    warningTimeRemaining: config.warningCountdownSeconds,
  });

  const sessionStartTime = useRef<number>(Date.now());
  const lastActivityTime = useRef<number>(Date.now());
  const sessionTimer = useRef<NodeJS.Timeout | null>(null);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const warningTimer = useRef<NodeJS.Timeout | null>(null);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (sessionTimer.current) {
      clearTimeout(sessionTimer.current);
      sessionTimer.current = null;
    }
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    console.log('🔐 Session expired - logging out');
    clearAllTimers();
    clearAuth();
    authUtils.clearTokens();
    setSessionState(prev => ({
      ...prev,
      isActive: false,
      isIdle: false,
      isWarning: false,
    }));
    navigate('/login', { replace: true });
  }, [clearAllTimers, navigate]);

  // Set up all timers
  const setupTimers = useCallback(() => {
    const now = Date.now();
    const sessionElapsed = (now - sessionStartTime.current) / 1000 / 60; // minutes
    const idleElapsed = (now - lastActivityTime.current) / 1000 / 60; // minutes

    console.log('🕐 Setting up timers:', {
      sessionElapsed: sessionElapsed.toFixed(2),
      idleElapsed: idleElapsed.toFixed(2),
      sessionTimeoutMinutes: config.sessionTimeoutMinutes,
      idleTimeoutMinutes: config.idleTimeoutMinutes,
    });

    // Check if total session time has expired
    if (sessionElapsed >= config.sessionTimeoutMinutes) {
      console.log('⏰ Session expired - total time exceeded');
      logout();
      return;
    }

    // Check if idle time has expired
    if (idleElapsed >= config.idleTimeoutMinutes) {
      console.log('⏰ Idle time expired - starting warning');
      setSessionState(prev => ({
        ...prev,
        isIdle: true,
        isWarning: true,
      }));

      // Start warning countdown
      let warningTimeLeft = config.warningCountdownSeconds;
      setSessionState(prev => ({
        ...prev,
        warningTimeRemaining: warningTimeLeft,
      }));

      warningTimer.current = setInterval(() => {
        warningTimeLeft -= 1;
        setSessionState(prev => ({
          ...prev,
          warningTimeRemaining: warningTimeLeft,
        }));

        if (warningTimeLeft <= 0) {
          logout();
        }
      }, 1000);
    } else {
      // Set up idle timer
      const idleTimeLeft = Math.max(0, (config.idleTimeoutMinutes - idleElapsed) * 60 * 1000);
      console.log('⏰ Setting idle timer for', (idleTimeLeft / 1000 / 60).toFixed(2), 'minutes');

      idleTimer.current = setTimeout(() => {
        console.log('⏰ Idle timer fired - starting warning');
        setSessionState(prev => ({
          ...prev,
          isIdle: true,
          isWarning: true,
        }));

        // Start warning countdown
        let warningTimeLeft = config.warningCountdownSeconds;
        setSessionState(prev => ({
          ...prev,
          warningTimeRemaining: warningTimeLeft,
        }));

        warningTimer.current = setInterval(() => {
          warningTimeLeft -= 1;
          setSessionState(prev => ({
            ...prev,
            warningTimeRemaining: warningTimeLeft,
          }));

          if (warningTimeLeft <= 0) {
            logout();
          }
        }, 1000);
      }, idleTimeLeft);
    }

    // Set up session timer (total session duration)
    const sessionTimeLeft = Math.max(
      0,
      (config.sessionTimeoutMinutes - sessionElapsed) * 60 * 1000
    );
    console.log(
      '⏰ Setting session timer for',
      (sessionTimeLeft / 1000 / 60).toFixed(2),
      'minutes'
    );

    sessionTimer.current = setTimeout(() => {
      console.log('⏰ Session timer fired - logging out');
      logout();
    }, sessionTimeLeft);
  }, [config, logout]);

  // Reset session timers
  const resetSession = useCallback(() => {
    console.log('🔄 Resetting session timers');
    clearAllTimers();
    const now = Date.now();
    lastActivityTime.current = now;
    sessionStartTime.current = now;

    setSessionState(prev => ({
      ...prev,
      isActive: true,
      isIdle: false,
      isWarning: false,
      timeRemaining: config.sessionTimeoutMinutes * 60,
      warningTimeRemaining: config.warningCountdownSeconds,
    }));

    // Set up new timers
    setupTimers();
  }, [config, clearAllTimers, setupTimers]);

  // Continue session (user clicked "Continue Session")
  const continueSession = useCallback(() => {
    console.log('✅ User chose to continue session');
    resetSession();
  }, [resetSession]);

  // Logout now (user clicked "Logout Now")
  const logoutNow = useCallback(() => {
    console.log('🚪 User chose to logout now');
    logout();
  }, [logout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = (now - lastActivityTime.current) / 1000 / 60; // minutes

    // Only reset if user has been active recently (within 1 minute)
    if (timeSinceLastActivity < 1) {
      return;
    }

    lastActivityTime.current = now;

    // If we're in warning mode and user is active, cancel warning
    if (sessionState.isWarning) {
      console.log('🔄 User activity detected during warning - canceling logout');
      clearAllTimers();
      setSessionState(prev => ({
        ...prev,
        isIdle: false,
        isWarning: false,
        warningTimeRemaining: config.warningCountdownSeconds,
      }));
      setupTimers();
    }
  }, [sessionState.isWarning, config, clearAllTimers, setupTimers]);

  // Initialize session on mount
  useEffect(() => {
    console.log('🚀 Initializing session manager');

    // Initialize timestamps
    const now = Date.now();
    sessionStartTime.current = now;
    lastActivityTime.current = now;

    setupTimers();

    // Set up activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const addActivityListeners = () => {
      events.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
    };

    const removeActivityListeners = () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };

    addActivityListeners();

    return () => {
      removeActivityListeners();
      clearAllTimers();
    };
  }, [setupTimers, handleActivity, clearAllTimers]);

  // Update time remaining display
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const sessionElapsed = (now - sessionStartTime.current) / 1000; // seconds
      const timeRemaining = Math.max(0, config.sessionTimeoutMinutes * 60 - sessionElapsed);

      setSessionState(prev => ({
        ...prev,
        timeRemaining: Math.floor(timeRemaining),
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [config.sessionTimeoutMinutes]);

  return {
    sessionState,
    continueSession,
    logoutNow,
    resetSession,
    handleActivity,
  };
}
